/**
 * Sport catalogue (TRAV-2 trip-reference rework) — a FIXED list in code, by client decision.
 *
 * Trip references are `{club_short_code}-{sport_short_code}-{YY}-{NNNN}` (e.g. `RCPB-HK-26-0001`),
 * so every trip needs a sport short code at reference-allocation time. The catalogue below is the
 * single source of truth: the request wizard offers exactly these sports (values = canonical
 * names), and {@link sportShortCode} folds whatever `trip.sport` actually stores — including the
 * legacy free-text values older trips carry — onto a code. `trip.sport` stays OPTIONAL: a trip
 * without a recognisable sport uses the {@link FALLBACK_SPORT_CODE} (`GEN`).
 *
 * Leaf module: imports nothing from its siblings (keep it cycle-safe under the barrel).
 */

export interface SportCatalogueEntry {
  /** Short code used in trip references, e.g. `HK`. */
  code: string;
  /** Canonical display name — also the value stored in `trip.sport`. */
  name: string;
}

/** The fixed sport catalogue (client-final). Order is the wizard's display order. */
export const SPORT_CATALOGUE: readonly SportCatalogueEntry[] = [
  { code: "HK", name: "Hockey" },
  { code: "PAD", name: "Padel" },
  { code: "HIP", name: "Hípica" },
] as const;

/** Canonical sport names, for pick-lists and the strict club-submit schema. */
export const SPORT_NAMES = SPORT_CATALOGUE.map((s) => s.name) as [string, ...string[]];

/** Reference code for trips with no (or an unrecognised) sport. */
export const FALLBACK_SPORT_CODE = "GEN";

/** Lowercase + strip diacritics + trim, so "Pádel", " padel " and "PADEL" all compare equal. */
function normalizeSportName(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/**
 * The catalogue entry a stored `trip.sport` value names, matched case- and diacritic-insensitively
 * ("hipica" → Hípica). `null` when the value is empty/unknown (legacy free text like "Tenis").
 */
export function findSport(sport: string | null | undefined): SportCatalogueEntry | null {
  if (!sport) return null;
  const needle = normalizeSportName(sport);
  if (!needle) return null;
  return SPORT_CATALOGUE.find((s) => normalizeSportName(s.name) === needle) ?? null;
}

/**
 * The canonical catalogue name for a stored `trip.sport` value, or `null` when it isn't a
 * catalogue sport — used to fold legacy free-text values onto the wizard's Select options.
 */
export function canonicalSportName(sport: string | null | undefined): string | null {
  return findSport(sport)?.name ?? null;
}

/**
 * The sport short code a trip reference uses for this stored `trip.sport` value. Unknown or
 * missing sports fall back to {@link FALLBACK_SPORT_CODE} — never throws.
 */
export function sportShortCode(sport: string | null | undefined): string {
  return findSport(sport)?.code ?? FALLBACK_SPORT_CODE;
}
